import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetProjectVersionsListQuery,
  GetProjectVersionsListData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetProjectVersionsListState
  extends MergeRequestState<GetProjectVersionsListData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getProjectVersionsList,
  isFetching: (state: ReduxState) =>
    state.server.getProjectVersionsList.isFetching,
  data: (state: ReduxState) => state.server.getProjectVersionsList.data,
  error: (state: ReduxState) => state.server.getProjectVersionsList.error,
};

const initialState: GetProjectVersionsListState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getProjectVersionsList';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      query,
      config,
    }: { query: GetProjectVersionsListQuery; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.getProjectVersionsList(query, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetProjectVersionsList {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getProjectVersionsList: ServerGetProjectVersionsList = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
